import { createStringSetCache } from './create-string-set-cache';

/**
 * Module-level memory of which link-list URLs have previously resolved
 * successfully via Smart Card.
 *
 * Why this exists:
 * The link-list items keep their "resolved" status in local React state and show
 * a skeleton overlay until the underlying Smart Card fires `onResolve`. That state
 * is tied to the component's mount lifetime, and the `SmartCardProvider` used by
 * the link list keeps its resolution store tied to its own mount lifetime too.
 *
 * Some consumers re-mount the link-list subtree very frequently while content
 * streams in (for example the inline Lumina link list in SAIN is re-rendered
 * on every streamed chunk, and the Rovo Extension chat panel re-mounts its tree as
 * the answer streams). Every remount would otherwise reset the local `resolved`
 * flag back to `false` and flash the loading skeleton again, producing a visible
 * resolved <-> loading flicker.
 *
 * By remembering, at module scope, that a given URL has resolved at least once, we
 * can initialise the per-item `resolved` state to `true` on a fresh mount. The
 * Smart Card still re-resolves in the background (typically synchronously from its
 * own warm store), but the user no longer sees the skeleton flash.
 *
 * Implementation, memory bound and SSR safety are provided by the shared
 * {@link createStringSetCache} primitive.
 */
const cache = createStringSetCache();

/**
 * Returns whether the given URL has previously resolved successfully.
 *
 * Always returns `false` on the server (see {@link createStringSetCache}).
 */
export const hasUrlResolved = (url: string | undefined): boolean => cache.has(url);

/**
 * Records that the given URL has resolved successfully.
 *
 * No-op on the server (see {@link createStringSetCache}).
 */
export const markUrlResolved = (url: string | undefined): void => cache.mark(url);

/**
 * Internal reset used only to clear the module-level cache.
 *
 * Do NOT call this from production code: flushing the cache mid-session would
 * reintroduce the loading skeleton flicker this cache prevents. It is intentionally
 * named with an `__unsafe` prefix and is only meant to be consumed via
 * `resolved-url-cache.test-utils.ts` (see `clearResolvedUrlCache` there).
 */
export const __unsafeResetResolvedUrlCache = (): void => cache.__unsafeReset();
